import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useUpdateCandidateNote } from '@/hooks/use-candidates';
import { extractErrorMessage } from '@/lib/errors';
import { cn } from '@/lib/utils';
import { EditorToolbar } from '@/components/ui/editor-toolbar';
import type { CandidateNote } from '@/types/candidates';
import { EditorContent, useEditor } from '@tiptap/react';
import { useEffect, useState } from 'react';
import { candidateNoteBaseExtensions } from './candidate-note-extensions';
import { useNoteMentionExtension } from './candidate-note-mention';

interface CandidateNoteEditDialogProps {
  note: CandidateNote | null;
  candidateId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Edit modal for a candidate note. Native and PeopleForce notes are both stored
 * as HTML (the composer persists `editor.getHTML()`), so the body is edited in
 * the same Tiptap surface the feed renders — never as raw markup in a textarea
 * (HW-591). Headings, links and @-mentions round-trip via `getHTML()` on save.
 */
export function CandidateNoteEditDialog({
  note,
  candidateId,
  open,
  onOpenChange,
}: CandidateNoteEditDialogProps) {
  const mutation = useUpdateCandidateNote(candidateId);
  const mention = useNoteMentionExtension();

  // `useEditor` doesn't re-render this component on every transaction, so the
  // Save gating is driven off `onUpdate` state (mirrors the composer) rather
  // than reading `editor.getHTML()` during render, which would go stale.
  const [html, setHtml] = useState('');
  const [isEmpty, setIsEmpty] = useState(true);

  const editor = useEditor({
    extensions: [...candidateNoteBaseExtensions({ openOnClick: false }), mention],
    content: note?.body ?? '',
    editable: true,
    editorProps: {
      attributes: {
        class: cn(
          'prose prose-sm dark:prose-invert max-w-none min-h-[280px] w-full',
          'bg-background px-3 py-2 text-sm leading-relaxed',
          'text-foreground focus:outline-none',
          '[&_p]:my-1 [&_ul]:my-1 [&_ol]:my-1',
          '[&_h2]:mt-3 [&_h2]:mb-1 [&_h2]:text-sm [&_h2]:font-semibold',
          '[&_h3]:mt-2 [&_h3]:mb-0.5 [&_h3]:text-xs [&_h3]:font-semibold [&_h3]:uppercase [&_h3]:tracking-wide [&_h3]:text-muted-foreground'
        ),
      },
    },
    onUpdate: ({ editor }) => {
      setHtml(editor.getHTML());
      setIsEmpty(editor.getText().trim().length === 0);
    },
  });

  // Re-seed when a different note is opened (the dialog instance is reused) or
  // when reopening after a cancelled edit, so the editor reflects the stored
  // body rather than a stale draft. `setContent` doesn't emit an update, so the
  // gating state is synced by hand here.
  useEffect(() => {
    if (open && note && editor) {
      editor.commands.setContent(note.body);
      setHtml(note.body);
      setIsEmpty(editor.getText().trim().length === 0);
    }
  }, [open, note, editor]);

  // Lock the editor while the save is in flight.
  useEffect(() => {
    editor?.setEditable(!mutation.isPending);
  }, [editor, mutation.isPending]);

  if (!note) return null;

  const isPF = note.source === 'peopleforce';
  const hasChanges = !isEmpty && html !== note.body;

  const handleSave = () => {
    if (!editor) return;
    // Re-derive mentioned employees from the document so adding/removing a
    // mention while editing keeps the notification targets in sync.
    const ids = new Set<string>();
    editor.state.doc.descendants((node) => {
      if (node.type.name === 'mention') {
        const id = node.attrs.id as string | null;
        if (id) ids.add(id);
      }
    });
    mutation.mutate(
      {
        noteId: note.id,
        input: {
          body: editor.getHTML(),
          mentionedEmployeeIds: ids.size > 0 ? Array.from(ids) : undefined,
        },
      },
      { onSuccess: () => onOpenChange(false) }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] flex-col max-w-2xl">
        <DialogHeader>
          <DialogTitle>Edit note</DialogTitle>
          <DialogDescription>
            {isPF ? (
              <>
                This note was imported from PeopleForce. Saving marks it as
                locally-edited so the next backfill won&apos;t overwrite your changes.
                Rich formatting (headings, lists, links) is preserved.
              </>
            ) : (
              <>Update the note body. Formatting, links and @-mentions are preserved.</>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-2 overflow-y-auto">
          <div className="overflow-hidden rounded-md border border-input">
            <EditorToolbar editor={editor} headingLevels={[2, 3]} />
            <EditorContent editor={editor} />
          </div>
          {mutation.isError && (
            <p className="text-xs text-destructive">
              {extractErrorMessage(mutation.error, 'Failed to save')}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button disabled={!hasChanges || mutation.isPending} onClick={handleSave}>
            {mutation.isPending ? 'Saving…' : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
